<template>
  <nav class="hidden lg:block">
    <div
      :class="[
        'transition-[background-color_translateY] duration-200 relative z-10 px-15 ease-linear',
        {
          'bg-dustrose': open || (!transparent && !collapsed),
          '-translate-y-full': collapsed,
        },
      ]"
    >
      <div class="flex items-center justify-between text-dustrose py-8">
        <div class="flex items-center gap-8 w-75">
          <MenuToggle
            :open="open"
            @click.stop="toggleOpen"
            :dark="!transparent"
            :disabled="!reorderedLinks.length"
          />
          <LangSwitch :dark="!transparent" />
        </div>
        <LangRouterLink :to="{ name: $routes.HOME }">
          <img :src="logo" alt="" class="w-25" />
        </LangRouterLink>
        <div class="flex justify-end items-center gap-10 w-75">
          <CardLink
            :class="{
              '[&_.icon]:text-burgundy!': !open && transparent,
              'text-burgundy': open || !transparent,
            }"
          />
          <BookingLink />
        </div>
      </div>
      <div
        :class="[
          'grid grid-rows-[0fr] transition-[grid-template-rows]',
          { 'grid-rows-[1fr]': open },
        ]"
      >
        <div class="overflow-hidden">
          <div
            class="border-t border-pink grid grid-cols-[33%_1fr] items-start overflow-hidden"
            :style="{
              height: `${71 * Math.ceil(reorderedLinks.length / 2)}px`,
            }"
          >
            <div class="p-2.5 pl-0 overflow-hidden h-full">
              <ResponsiveImage :media="image" class="w-full h-full object-cover" />
            </div>
            <ul class="grid grid-cols-2">
              <li
                v-for="(link, linkIndex) in reorderedLinks"
                :key="`desktop-link-${linkIndex}`"
                :class="[
                  'border-pink border-b border-r odd:border-l',
                  {
                    'border-b-0':
                      linkIndex >=
                      reorderedLinks.length - (reorderedLinks.length % 2 === 0 ? 2 : 1),
                  },
                ]"
              >
                <SiteLink
                  :content="link"
                  class="px-8 py-5.5 block transition-[background-color] hover:bg-pink"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <Transition name="fade">
      <div
        class="w-full h-[100dvh] bg-black/55 top-0 left-0 absolute cursor-pointer"
        v-if="open"
        @click.stop="close"
      ></div>
    </Transition>
  </nav>
</template>

<script lang="ts">
import { computed, type PropType, toRef } from 'vue'

import LangRouterLink from '@/components/helpers/LangRouterLink.vue'
import ResponsiveImage from '@/components/helpers/ResponsiveImage.vue'
import SiteLink from '@/components/helpers/SiteLink.vue'
import BookingLink from '@/components/navigation/small/BookingLink.vue'
import CardLink from '@/components/navigation/small/CardLink.vue'
import LangSwitch from '@/components/navigation/small/LangSwitch.vue'
import MenuToggle from '@/components/navigation/small/MenuToggle.vue'
import { useNavigation } from '@/composables/useNavigation.ts'

export default {
  name: 'DesktopNavigation',
  components: {
    BookingLink,
    CardLink,
    LangRouterLink,
    LangSwitch,
    MenuToggle,
    ResponsiveImage,
    SiteLink,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    image: {
      type: Object as PropType<Media>,
      required: true,
    },
    links: {
      type: Array as PropType<Link[]>,
      required: true,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { logo, open, close, toggleOpen } = useNavigation(toRef(props, 'transparent'))

    const reorderedLinks = computed(() => {
      const links = [...props.links]
      const middle = Math.ceil(links.length / 2)
      const left = links.slice(0, middle)
      const right = links.slice(middle)

      return left.flatMap((item, i) => [item, right[i]].filter(Boolean))
    })

    return { logo, open, reorderedLinks, close, toggleOpen }
  },
}
</script>
